import { useState } from 'react';
import { api } from '../lib/api.js';
import { HourlyTempChart } from './HourlyTempChart.jsx';
import { todayStr } from '../lib/format.js';

// ─── Equipment View ────────────────────────────────────────────────────────────
// Compliance-ready temperature report (CJ 2026-05-16). Managers download
// a CSV for Dept of Health, optionally scoped to one piece of equipment,
// the current location, or all locations. The hourly-average chart sits
// below the controls — one line per equipment, with green target band.
export function TemperatureReportPanel({ equipment, locationId, locationName, isOwner, user }) {
  const [open, setOpen] = React.useState(false);
  const [scope, setScope] = React.useState('location'); // 'all' | 'location' | 'equipment'
  const [eqId, setEqId] = React.useState('');
  const [days, setDays] = React.useState(30);
  const [report, setReport] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');

  function buildParams() {
    const p = new URLSearchParams();
    p.set('scope', scope);
    p.set('days', String(days));
    if (scope === 'location') p.set('id', String(locationId));
    if (scope === 'equipment' && eqId) p.set('id', String(eqId));
    return p;
  }

  async function loadReport() {
    setBusy(true); setErr('');
    try {
      const r = await api('GET', `/api/admin/temperatures/report?${buildParams().toString()}`,
        null, { 'x-user-id': String(user.id) });
      setReport(r);
    } catch (e) { setErr(e.message || String(e)); setReport(null); }
    finally { setBusy(false); }
  }

  function downloadCsv() {
    if (scope === 'equipment' && !eqId) { setErr('Pick a piece of equipment.'); return; }
    const params = buildParams();
    params.set('format', 'csv');
    const url = `/api/admin/temperatures/report?${params.toString()}`;
    fetch(url, { headers: { 'x-user-id': String(user.id) } })
      .then(r => r.ok ? r.blob() : Promise.reject(new Error('Download failed')))
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        const stamp = todayStr();
        a.download = `temperature-log_${scope}_${days}d_${stamp}.csv`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      })
      .catch(e => setErr(e.message || 'Download failed'));
  }

  return (
    <div className="card mb-4" style={{ padding: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#212121' }}>🌡 Temperature report</div>
          <div style={{ fontSize: 11, color: '#757575' }}>
            Download a 30-day CSV for the Dept of Health, or see an hourly-average chart per equipment.
          </div>
        </div>
        <button onClick={() => setOpen(o => !o)}
          className="btn-secondary" style={{ fontSize: 12 }}>
          {open ? 'Hide' : 'Open'}
        </button>
      </div>
      {open && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
            <select value={scope} onChange={e => { setScope(e.target.value); setReport(null); }}
              style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: '6px 10px', fontSize: 13 }}>
              <option value="location">This location ({locationName})</option>
              <option value="equipment">One piece of equipment</option>
              {isOwner && <option value="all">All locations</option>}
            </select>
            {scope === 'equipment' && (
              <select value={eqId} onChange={e => { setEqId(e.target.value); setReport(null); }}
                style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: '6px 10px', fontSize: 13 }}>
                <option value="">— pick equipment —</option>
                {equipment.map(eq => (
                  <option key={eq.id} value={eq.id}>{eq.name}</option>
                ))}
              </select>
            )}
            <select value={days} onChange={e => { setDays(Number(e.target.value)); setReport(null); }}
              style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: '6px 10px', fontSize: 13 }}>
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
            </select>
            <button onClick={loadReport} disabled={busy || (scope === 'equipment' && !eqId)}
              style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                        background: 'var(--forest)', color: '#fff', border: 'none', borderRadius: 6,
                        cursor: 'pointer', opacity: busy ? 0.5 : 1 }}>
              {busy ? 'Loading…' : 'Show chart'}
            </button>
            <button onClick={downloadCsv}
              style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                        background: '#fff', color: '#1565c0', border: '1px solid #1565c0', borderRadius: 6,
                        cursor: 'pointer' }}>
              ⬇ Download CSV
            </button>
          </div>
          {err && <div style={{ fontSize: 12, color: '#c62828', marginTop: 4 }}>{err}</div>}
          {report && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>
                <strong>{report.summary.total_readings}</strong> readings ·
                {' '}<strong>{report.summary.equipment_count}</strong> equipment ·
                {' '}<strong style={{ color: report.summary.out_of_range > 0 ? '#c62828' : '#2e7d32' }}>
                  {report.summary.out_of_range}
                </strong> out-of-range
              </div>
              <HourlyTempChart hourlyAverages={report.hourlyAverages} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
